import { useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { clearToken } from '../lib/auth'
import {
  LayoutDashboard, Users, Bell, ShieldCheck, Clock,
  LogOut, Stethoscope, Plus, BookOpen, Database, GitFork, Menu, X,
} from 'lucide-react'
import clsx from 'clsx'

const nav = [
  { to: '/app', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/app/graph', label: 'Knowledge Graph', icon: GitFork },
  { to: '/app/timeline', label: 'Timeline', icon: Clock },
  { to: '/app/family', label: 'Family Graph', icon: Users },
  { to: '/app/alerts', label: 'Alerts', icon: Bell },
  { to: '/app/brief', label: 'Doctor Brief', icon: Stethoscope },
  { to: '/app/consent', label: 'Consent', icon: ShieldCheck },
  { to: '/app/ingest', label: 'Add Data', icon: Plus },
  { to: '/app/engine', label: 'DAS Engine', icon: Database },
  { to: '/app/report', label: 'Dev Report', icon: BookOpen },
]

function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const logout = () => { clearToken(); navigate('/') }

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside className={clsx(
        'fixed inset-y-0 left-0 z-30 w-56 bg-brand-900 text-white flex flex-col transition-transform duration-200',
        'lg:static lg:translate-x-0',
        open ? 'translate-x-0' : '-translate-x-full',
      )}>
        <div className="px-5 py-4 border-b border-brand-700 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold tracking-tight">FamilyPortal</h1>
            <p className="text-xs text-brand-100 mt-0.5">Health Reasoning Engine</p>
          </div>
          <button onClick={onClose} className="lg:hidden text-brand-300 hover:text-white p-1">
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {nav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/app'}
              onClick={onClose}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-brand-600 text-white'
                    : 'text-brand-100 hover:bg-brand-700',
                )
              }
            >
              <Icon size={16} />
              {label}
            </NavLink>
          ))}
        </nav>
        <button
          onClick={logout}
          className="flex items-center gap-3 mx-3 mb-4 px-3 py-2 rounded-lg text-sm text-brand-100 hover:bg-brand-700 transition-colors"
        >
          <LogOut size={16} /> Sign out
        </button>
      </aside>
    </>
  )
}

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex flex-col flex-1 min-w-0">
        {/* Mobile top bar */}
        <header className="lg:hidden flex items-center gap-3 px-4 py-3 bg-brand-900 border-b border-brand-700 shrink-0">
          <button onClick={() => setSidebarOpen(true)} className="text-brand-100 hover:text-white p-1">
            <Menu size={20} />
          </button>
          <span className="text-white font-bold text-base tracking-tight">FamilyPortal</span>
        </header>

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
